import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { Spinner } from '../../ui';
import { useSession } from './useSession';

/**
 * The routing guard. It asks the SERVER who is signed in (`GET /api/auth/me`) rather than
 * trusting anything in the browser: the session is an httpOnly cookie, so the only honest
 * answer comes from the server, and an expired one has to send the user to the login screen
 * even though the browser still holds a cookie.
 *
 * The pending state is a spinner, not a redirect. Redirecting while the answer is in flight
 * would flash the login screen on every cold load of every deep link.
 */
export function RequireAuth({ children }: { readonly children: ReactNode }) {
  const session = useSession();
  const location = useLocation();

  if (session.isPending) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-canvas" aria-busy="true">
        <Spinner label="Checking your session" />
      </div>
    );
  }

  if (session.data === null || session.data === undefined) {
    // Where they were going, so the login can send them back there.
    return (
      <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
    );
  }

  return children;
}
